/**
 * Builds {@link AggregatedJobData} for SRP SEO from a single Algolia response
 * (hits + facets). Fills salary, employers (human-readable names), fields, and
 * sample-based language/remote/full-time percentages so meta descriptions are
 * data-rich on location and other filtered pages.
 */

import type { AggregatedJobData } from '../../types/srp-seo-meta';
import type { FacetsData } from '../facets/facets-shared';
import { extractFacetsFromSearchResults } from '../facets/facets-shared';
import { buildTopEmployersStripFallbackFromSearch } from '../srp/build-top-employers-strip-fallback-from-search';
import { fmtSalary } from './generate-srp-seo-meta';

const MIN_HITS_FOR_SAMPLE_PCTS = 3;

function getTopFacetLabels(facetsData: FacetsData, attribute: string, n: number): string[] {
  const values = facetsData[attribute];
  if (!Array.isArray(values)) return [];
  return [...values]
    .sort((a: { count: number }, b: { count: number }) => b.count - a.count)
    .slice(0, n)
    .map((v: { label?: string; value: string }) => v.label || v.value)
    .filter(Boolean);
}

function midSalaryFromHit(hit: Record<string, unknown>): number | null {
  const min = hit.salaryRangeMin;
  const max = hit.salaryRangeMax;
  if (typeof min !== 'number' || typeof max !== 'number' || min <= 0 || max <= 0) return null;
  return (min + max) / 2;
}

/**
 * Prefer Algolia facetStats (full result set); fall back to the current page of hits.
 */
function averageSalaryFromFacetStatsOrHits(
  facetStats: Record<string, { avg?: number; min?: number; max?: number }> | undefined,
  hits: Record<string, unknown>[],
): number | null {
  const avg = facetStats?.salaryRangeMin?.avg;
  if (typeof avg === 'number' && Number.isFinite(avg) && avg > 0) {
    return Math.round(avg);
  }
  const mids: number[] = [];
  for (const h of hits) {
    const m = midSalaryFromHit(h);
    if (m != null) mids.push(m);
  }
  if (mids.length === 0) return null;
  return Math.round(mids.reduce((a, b) => a + b, 0) / mids.length);
}

function salaryRangeLabelFromFacetStatsOrHits(
  facetStats: Record<string, { min?: number; max?: number }> | undefined,
  hits: Record<string, unknown>[],
): string | null {
  const fsMin = facetStats?.salaryRangeMin?.min;
  const fsMax = facetStats?.salaryRangeMax?.max ?? facetStats?.salaryRangeMin?.max;
  if (typeof fsMin === 'number' && typeof fsMax === 'number' && fsMin > 0 && fsMax > 0) {
    return `€${fmtSalary(fsMin)} – €${fmtSalary(fsMax)}`;
  }

  let minV = Infinity;
  let maxV = -Infinity;
  for (const h of hits) {
    const min = h.salaryRangeMin;
    const max = h.salaryRangeMax;
    if (typeof min === 'number' && typeof max === 'number' && min > 0 && max > 0) {
      minV = Math.min(minV, min);
      maxV = Math.max(maxV, max);
    }
  }
  if (!Number.isFinite(minV) || !Number.isFinite(maxV)) return null;
  return `€${fmtSalary(minV)} – €${fmtSalary(maxV)}`;
}

function hitMentionsEnglish(hit: Record<string, unknown>): boolean {
  const langs = hit.requiredLanguages;
  if (Array.isArray(langs)) {
    return langs.some((l) => {
      const s = String(l).toLowerCase();
      return s.includes('english') || s === 'en' || s.includes('englanti');
    });
  }
  if (typeof langs === 'string') {
    const s = langs.toLowerCase();
    return s.includes('english') || s.includes('englanti');
  }
  return false;
}

function hitIsRemoteOrHybrid(hit: Record<string, unknown>): boolean {
  const r = String(hit.remoteness ?? '').toLowerCase();
  return r.includes('remote') || r.includes('hybrid') || r.includes('etä') || r.includes('distans');
}

function hitIsFullTime(hit: Record<string, unknown>): boolean {
  const jt = String(hit.jobType ?? '').toLowerCase();
  return jt.includes('full') || jt.includes('kokoaika') || jt.includes('heltid');
}

function samplePercentages(hits: Record<string, unknown>[]): Pick<
  AggregatedJobData,
  'englishPct' | 'remotePct' | 'fullTimePct'
> {
  if (hits.length < MIN_HITS_FOR_SAMPLE_PCTS) {
    return { englishPct: null, remotePct: null, fullTimePct: null };
  }
  let englishWithLang = 0;
  let langDenom = 0;
  let remote = 0;
  let fullTime = 0;
  const n = hits.length;
  for (const h of hits) {
    const hasLang =
      h.requiredLanguages != null &&
      (Array.isArray(h.requiredLanguages)
        ? h.requiredLanguages.length > 0
        : String(h.requiredLanguages).length > 0);
    if (hasLang) {
      langDenom++;
      if (hitMentionsEnglish(h)) englishWithLang++;
    }
    if (hitIsRemoteOrHybrid(h)) remote++;
    if (hitIsFullTime(h)) fullTime++;
  }
  return {
    englishPct: langDenom > 0 ? Math.round((englishWithLang / langDenom) * 100) : null,
    remotePct: Math.round((remote / n) * 100),
    fullTimePct: Math.round((fullTime / n) * 100),
  };
}

export function buildAggregatedJobDataFromSearchResults(
  searchResults: unknown,
  currentFacetFilters: Record<string, string[]> = {},
): Omit<AggregatedJobData, 'count'> {
  const sr = searchResults as {
    hits?: Record<string, unknown>[];
    facets?: Record<string, Record<string, number>>;
    facetStats?: Record<string, { min?: number; max?: number; avg?: number }>;
  } | null;

  if (!sr) {
    return {
      avgSalary: null,
      salaryMin: null,
      salaryMax: null,
      topEmployers: [],
      topCities: [],
      topFields: [],
      topSkills: [],
      topBenefits: [],
      englishPct: null,
      remotePct: null,
      fullTimePct: null,
      salaryRangeLabel: null,
    };
  }

  const hits = Array.isArray(sr.hits) ? sr.hits : [];
  const facetsData = extractFacetsFromSearchResults(sr as any, currentFacetFilters);

  const strip = buildTopEmployersStripFallbackFromSearch(facetsData, hits, 8);
  const topEmployers = strip.map((s) => s.name).filter((n) => n && n.trim()).slice(0, 3);

  const facetStats = sr.facetStats;

  const avgSalary = averageSalaryFromFacetStatsOrHits(facetStats, hits);
  const salaryRangeLabel = salaryRangeLabelFromFacetStatsOrHits(facetStats, hits);

  const pcts = samplePercentages(hits);

  return {
    avgSalary,
    salaryMin: typeof facetStats?.salaryRangeMin?.min === 'number' ? facetStats.salaryRangeMin.min : null,
    salaryMax: typeof facetStats?.salaryRangeMax?.max === 'number' ? facetStats.salaryRangeMax.max : null,
    topEmployers,
    topCities: getTopFacetLabels(facetsData, 'location', 3),
    topFields: getTopFacetLabels(facetsData, 'companyIndustry', 3),
    topSkills: getTopFacetLabels(facetsData, 'skillsAndTechnologies', 3),
    topBenefits: getTopFacetLabels(facetsData, 'benefits', 3),
    englishPct: pcts.englishPct,
    remotePct: pcts.remotePct,
    fullTimePct: pcts.fullTimePct,
    salaryRangeLabel,
  };
}
